import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Image as ImageIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import brandingAPI from '../services/brandingService';
import { AppBrandingInput } from '../types/branding';
import { useBranding } from '../hooks/useBranding';
import { DEFAULT_BRANDING } from '../constants/branding';
import AppBrand from './AppBrand';

const BrandingSettings: React.FC = () => {
  const { branding, refreshBranding, setBranding } = useBranding();
  const [formData, setFormData] = useState<AppBrandingInput>({
    site_title: '',
    site_short_title: '',
    site_subtitle: '',
    copyright_text: '',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    setFormData({
      site_title: branding.site_title,
      site_short_title: branding.site_short_title,
      site_subtitle: branding.site_subtitle,
      copyright_text: branding.copyright_text,
    });
  }, [branding]);

  const loadBranding = async () => {
    try {
      setLoading(true);
      setError('');
      await refreshBranding();
    } catch (err: any) {
      setError(err.message || 'Gagal memuat pengaturan branding');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const response = await brandingAPI.updateBranding(formData);
      if (response.success) {
        setBranding(response.data);
        setSuccess('Pengaturan branding berhasil disimpan');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Gagal menyimpan pengaturan');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      setError('');
      setSuccess('');
      const response = await brandingAPI.uploadLogo(file);
      if (response.success) {
        setBranding(response.data);
        setSuccess('Logo berhasil diunggah');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Gagal mengunggah logo');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleRemoveLogo = async () => {
    try {
      setRemoving(true);
      setError('');
      setSuccess('');
      const response = await brandingAPI.removeLogo();
      if (response.success) {
        setBranding(response.data);
        setSuccess('Logo dikembalikan ke default');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Gagal menghapus logo');
    } finally {
      setRemoving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      <Card>
        <CardHeader
          title="Tampilan Website"
          subheader="Logo dan judul yang ditampilkan di halaman login dan sidebar"
          action={
            <Button startIcon={<RefreshIcon />} onClick={loadBranding} disabled={loading}>
              Muat Ulang
            </Button>
          }
        />
        <CardContent>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Pratinjau
              </Typography>
              <Box
                sx={{
                  p: 2,
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'background.default',
                }}
              >
                <AppBrand variant="sidebar" />
              </Box>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
              <Box
                component="img"
                src={branding.logo_url || DEFAULT_BRANDING.logoUrl}
                alt="Logo"
                sx={{
                  width: 96,
                  height: 96,
                  objectFit: 'contain',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  p: 1,
                }}
              />
              <Stack spacing={1}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<ImageIcon />}
                  disabled={uploading}
                >
                  {uploading ? 'Mengunggah...' : 'Unggah Logo'}
                  <input type="file" hidden accept="image/*" onChange={handleLogoChange} />
                </Button>
                {branding.logo_path && (
                  <Button
                    variant="text"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleRemoveLogo}
                    disabled={removing}
                  >
                    Hapus Logo Kustom
                  </Button>
                )}
                <Typography variant="caption" color="text.secondary">
                  PNG, JPG, SVG, WEBP, GIF. Maks. 2 MB.
                </Typography>
              </Stack>
            </Stack>

            <TextField
              label="Judul Website"
              value={formData.site_title}
              onChange={(e) => setFormData((prev) => ({ ...prev, site_title: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Judul Singkat (AppBar)"
              value={formData.site_short_title}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, site_short_title: e.target.value }))
              }
              fullWidth
              required
            />
            <TextField
              label="Subjudul"
              value={formData.site_subtitle}
              onChange={(e) => setFormData((prev) => ({ ...prev, site_subtitle: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Teks Copyright"
              value={formData.copyright_text}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, copyright_text: e.target.value }))
              }
              fullWidth
            />

            <Box>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Menyimpan...' : 'Simpan Pengaturan'}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default BrandingSettings;
